import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import {
  AccountInfo,
  AccountSummary,
  getAccountInfo,
  getAccountSummary,
  PaymentInfo,
  toAccountSummary,
} from '@features/auth/services/AccountService';

export interface PaymentsState {
  loading: boolean;
  isUpgradeDialogOpen: boolean;
  isUpdatePaymentMethodOpen: boolean;
  // Full billing details, company admins only: loaded by the payments page.
  accountInfo?: AccountInfo;
  // Limits and counts for everyone: set from the app config, refreshed after adding users or teams.
  accountSummary?: AccountSummary;
  paymentInfo?: PaymentInfo;
}

export const initialPaymentsState: PaymentsState = {
  loading: false,
  isUpgradeDialogOpen: false,
  isUpdatePaymentMethodOpen: false,
}

export const loadAccountInfo: any = createAsyncThunk(
  'payments/loadAccountInfo',
  async (force: any, thunkAPI) => {
    const accountInfo = await getAccountInfo();
    return accountInfo;
  }
)

export const loadAccountSummary: any = createAsyncThunk(
  'payments/loadAccountSummary',
  async () => await getAccountSummary()
)

const paymentsSlice = createSlice({
  name: 'payments',
  initialState: initialPaymentsState,
  reducers: {
    reset: () => initialPaymentsState,
    clearHolidays: () => initialPaymentsState,
    closeUpgradeDialog: (state) => ({...state, isUpgradeDialogOpen: false}),
    openUpgradeDialog: (state) => ({...state, isUpgradeDialogOpen: true}),
    closeUpdatePaymentMethodDialog: (state) => ({...state, isUpdatePaymentMethodOpen: false}),
    openUpdatePaymentMethodDialog: (state) => ({...state, isUpdatePaymentMethodOpen: true}),
    setAccountSummary: (state, action) => ({...state, accountSummary: action.payload}),
  },
  extraReducers: (builder) => {
    // Plan and seat changes on the payments page reload the full info, so keep the summary in step.
    builder.addCase(loadAccountInfo.fulfilled, (state, action) => ({
      ...state,
      accountInfo: action.payload,
      accountSummary: toAccountSummary(action.payload),
    }));
    builder.addCase(loadAccountSummary.fulfilled, (state, action) => ({...state, accountSummary: action.payload}));
  }
});

export const { setAccountSummary } = paymentsSlice.actions;
export default paymentsSlice.reducer;

// Selectors
const isLoading = (state: AppState) => state.payments.loading;
export const selectAccountInfo = (state: AppState) => state.payments.accountInfo;
export const selectAccountSummary = (state: AppState) => state.payments.accountSummary;
export const selectIsAccountActive = (state: AppState) => state.payments.accountInfo?.active;
export const selectNumberOfRemainingUsers = (state: AppState) => (state.payments.accountSummary?.userLimit ?? 0) - (state.payments.accountSummary?.totalUsers ?? 0);
export const selectIsFreeAccount = (state: AppState) => !state.payments.accountSummary?.paidAccount;
export const selectIsAccountLimitReached = (state: AppState) => (state.payments.accountSummary?.userLimit ?? 0) - (state.payments.accountSummary?.totalUsers ?? 0) < 1;
